import { useState, useRef, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { topicsApi, authApi, schoolsApi, coursesApi } from "../../../services/api"
import Button from "../../../components/ui/Button"
import Card, { CardHeader, CardBody } from "../../../components/ui/Card"
import { Field, Select, Textarea } from "../../../components/ui/Field"

const TOPIC_TYPES = [
  'Undergraduate Final Year Project',
  "Master's Thesis",
  'PhD Thesis',
  'PGD Final Project'
]
const IDEA_PACK_AMOUNT_NGN = 2000
const IDEA_PACK_SIZE = 10
function normalizeTopicFeatures (features) {
  if (!Array.isArray(features)) return []
  return features
    .map((item) => {
      if (typeof item === "string") return item.trim()
      if (item && typeof item === "object") return String(item.title || item.name || item.feature || "").trim()
      return String(item || "").trim()
    })
    .filter(Boolean)
}

function GeneratedTopicDetails ({ topic }) {
  const features = normalizeTopicFeatures(topic.features)
  const hasDetails = topic.summary || features.length > 0 || topic.explanation

  if (!hasDetails) {
    return (
      <p className="whitespace-pre-wrap text-slate-700 leading-relaxed">
        {topic.description || topic.title}
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {topic.summary && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Summary</p>
          <p className="whitespace-pre-wrap text-slate-700 leading-relaxed">{topic.summary}</p>
        </div>
      )}
      {features.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Features</p>
          <ul className="list-disc list-inside space-y-1 text-slate-700">
            {features.map((f, i) => (
              <li key={i} className="leading-relaxed">{f}</li>
            ))}
          </ul>
        </div>
      )}
      {topic.explanation && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Explanation</p>
          <p className="whitespace-pre-wrap text-slate-700 leading-relaxed">{topic.explanation}</p>
        </div>
      )}
    </div>
  )
}

const TopicIdeas = ({ setPage, page }) => {
  const navigate = useNavigate()
  const [schools, setSchools] = useState([])
  const [courses, setCourses] = useState([])
  const [schoolId, setSchoolId] = useState("")
  const [school, setSchool] = useState("")
  const [courseId, setCourseId] = useState("")
  const [course, setCourse] = useState("")
  const [projectType, setProjectType] = useState("")
  const [preference, setPreference] = useState("")
  const [loading, setLoading] = useState(false)
  const [profileLoading, setProfileLoading] = useState(false)
  const [error, setError] = useState("")
  const [successMessage, setSuccessMessage] = useState("")
  const [generatedTopics, setGeneratedTopics] = useState([])
  const [usingMyDetails, setUsingMyDetails] = useState(false)
  const [ideaGenerationsUsed, setIdeaGenerationsUsed] = useState(null)
  const [ideaGenerationsAllowed, setIdeaGenerationsAllowed] = useState(null)
  const [ideaLimitReached, setIdeaLimitReached] = useState(false)
  const [expandedTopicKeys, setExpandedTopicKeys] = useState(new Set())
  const [savingTopicKey, setSavingTopicKey] = useState(null)
  const generatedRef = useRef(null)

  useEffect(() => {
    schoolsApi.list().then(res => setSchools(res?.data?.data || [])).catch(() => setSchools([]))
    coursesApi.list().then(res => setCourses(res?.data?.data || [])).catch(() => setCourses([]))
  }, [])

  useEffect(() => {
    const s = schools.find(x => x._id === schoolId)
    setSchool(s?.name || "")
  }, [schoolId, schools])

  const handleUseMyDetails = async () => {
    setProfileLoading(true)
    setError("")
    try {
      const res = await authApi.me()
      if (res?.data?.data) {
        const p = res.data.data
        setSchool(p.school || "")
        const dept = (p.department || "").trim()
        setCourse(dept)
        const match = courses.find(c => (c.name || "").trim().toLowerCase() === dept.toLowerCase())
        setCourseId(match?._id || "")
        const l = (p.level || "").toString().trim().toLowerCase()
        if (l === "masters" || l === "msc" || l.includes("master")) setProjectType("Master's Thesis")
        else if (l.includes("phd")) setProjectType("PhD Thesis")
        else if (l.includes("pgd")) setProjectType("PGD Final Project")
        else if (["100", "200", "300", "400", "500"].includes(l) || /^\d+$/.test(l)) setProjectType("Undergraduate Final Year Project")
        else setProjectType("Undergraduate Final Year Project")
        setUsingMyDetails(true)
        const schoolMatch = schools.find(s => (s.name || "").trim() === (p.school || "").trim())
        if (schoolMatch) setSchoolId(schoolMatch._id)
        else setSchoolId("")
      }
    } catch (e) {
      setError(e?.response?.data?.message || "Could not load your profile")
    } finally {
      setProfileLoading(false)
    }
  }

  const handleClearForOther = () => {
    setSchoolId("")
    setSchool("")
    setCourseId("")
    setCourse("")
    setProjectType("")
    setPreference("")
    setUsingMyDetails(false)
    setError("")
  }

  const handleGenerate = async () => {
    if (!school.trim() || !course.trim() || !projectType) {
      setError("Please fill School, Course and Topic Type")
      return
    }
    setError("")
    setSuccessMessage("")
    setLoading(true)
    try {
      const res = await topicsApi.generate({
        school: school.trim(),
        ...(courseId ? { courseId } : { course: course.trim() }),
        projectType,
        preference: preference.trim() || undefined,
        save: false
      })
      const data = res?.data?.data || {}
      const newTopics = data.topics || []
      const count = newTopics.length
      setIdeaLimitReached(false)
      setIdeaGenerationsUsed(data.ideaGenerationsUsed ?? null)
      setIdeaGenerationsAllowed(data.ideaGenerationsAllowed ?? null)
      if (count > 0) {
        setExpandedTopicKeys(new Set())
        setGeneratedTopics(newTopics)
        setSuccessMessage(`${count} topic${count !== 1 ? 's' : ''} generated. Save the ones you like below.`)
        setTimeout(() => generatedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100)
      } else {
        setExpandedTopicKeys(new Set())
        setGeneratedTopics([])
      }
    } catch (e) {
      if (e?.response?.status === 402 && e?.response?.data?.code === 'IDEA_LIMIT') {
        setIdeaLimitReached(true)
        setError('')
        setIdeaGenerationsUsed(e?.response?.data?.data?.ideaGenerationsUsed ?? null)
        setIdeaGenerationsAllowed(e?.response?.data?.data?.ideaGenerationsAllowed ?? null)
      } else {
        setIdeaLimitReached(false)
        setError(e?.response?.data?.message || "Failed to generate topics")
      }
    } finally {
      setLoading(false)
    }
  }

  const toggleTopicExpanded = (key) => {
    setExpandedTopicKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const topicDescriptionForSave = (topic) => {
    const raw = (topic.description || topic.summary || topic.title || "").trim()
    if (raw.length >= 5) return raw
    return `${raw || "Topic idea"}.`
  }

  const handleSaveTopic = async (topic, index) => {
    if (topic._id) return
    const key = `gen-${index}-${topic.title}`
    setSavingTopicKey(key)
    setError("")
    try {
      const res = await topicsApi.create({
        title: topic.title,
        description: topicDescriptionForSave(topic),
        summary: topic.summary,
        features: normalizeTopicFeatures(topic.features),
        explanation: topic.explanation,
        category: "Other",
        difficulty: "Intermediate"
      })
      const saved = res?.data?.data
      if (saved?._id) {
        setGeneratedTopics((prev) =>
          prev.map((t, i) => (i === index ? { ...t, _id: saved._id } : t))
        )
      }
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to save topic")
    } finally {
      setSavingTopicKey(null)
    }
  }

  return (
    <div className="flex flex-col w-full">
      <div className="flex items-center gap-4 mb-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setPage("SavedIdeas")}
          className="gap-1"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 15.75 3 12m0 0 3.75-3.75M3 12h18" />
          </svg>
          Back to Saved ideas
        </Button>
      </div>
      <Card className="w-full shadow-soft">
        <CardHeader
          title="Generate 100+ ideas for your final year project"
          subtitle="Use your own profile details or enter details for another student."
        />
        <CardBody className="pt-2">
          {ideaGenerationsUsed !== null && (
            <p className="text-sm text-slate-700 mb-2">
              You have used {ideaGenerationsUsed} idea generations{ideaGenerationsAllowed !== null ? ` of ${ideaGenerationsAllowed} allowed` : ""}.
            </p>
          )}
          {ideaLimitReached && (
            <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <p className="text-sm text-amber-900">
                  You have reached your topic generation limit. Pay &#8358;{IDEA_PACK_AMOUNT_NGN.toLocaleString()} to unlock {IDEA_PACK_SIZE} more idea generations.
                </p>
                <div className="flex-1" />
                <Button size="sm" onClick={() => navigate('/pricing?ideaPack=1')}>
                  Pay {IDEA_PACK_AMOUNT_NGN.toLocaleString()} and unlock
                </Button>
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-2 mb-4">
            <Button variant="outline" size="sm" disabled={profileLoading} onClick={handleUseMyDetails}>
              {profileLoading ? "Loading..." : "Use my profile details"}
            </Button>
            <Button variant="ghost" size="sm" onClick={handleClearForOther}>
              Clear — enter for another student
            </Button>
          </div>
          {usingMyDetails && <p className="text-sm text-green-700 mb-2">Using your profile. You can still edit below or clear to enter for someone else.</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="School">
              <Select
                value={schoolId}
                onChange={(e) => {
                  setSchoolId(e.target.value)
                  setUsingMyDetails(false)
                }}
              >
                <option value="">Select school</option>
                {schools.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="Course / Department">
              <Select
                value={courseId || course}
                onChange={(e) => {
                  const next = e.target.value
                  const c = courses.find((x) => String(x._id) === String(next))
                  if (c) {
                    setCourseId(String(c._id))
                    setCourse(c.name || "")
                  } else {
                    // fallback if a raw value is used (e.g. profile department not in list)
                    setCourseId("")
                    setCourse(next)
                  }
                  setUsingMyDetails(false)
                }}
              >
                <option value="">Select department</option>
                {courses.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="Topic Type">
              <Select
                value={projectType}
                onChange={(e) => {
                  setProjectType(e.target.value)
                  setUsingMyDetails(false)
                }}
              >
                <option value="">Select topic type</option>
                {TOPIC_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="Tell us around which aspect you want your projec to be (optional)">
              <Textarea
                placeholder="e.g. Project around machine learning"
                rows={2}
                value={preference}
                onChange={(e) => setPreference(e.target.value)}
              />
            </Field>
            {error && <p className="text-red-600 text-sm">{error}</p>}
            <div className="sm:col-span-2 pt-1">
              <Button variant="primary" className="w-full" loading={loading} onClick={handleGenerate}>
                Generate now
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>
      <div ref={generatedRef} />
      <Card className="flex flex-col mt-4 shadow-soft">
        <CardHeader
          title="Generated topics"
          subtitle="Review generated ideas and save the ones you want to keep."
        />
        <CardBody className="pt-2">
        {successMessage && <p className="text-green-700 text-sm mt-2 font-medium">{successMessage}</p>}
        <div className="flex flex-col space-y-4 mt-4">
          {generatedTopics.length === 0 && !loading && <p className="text-slate-600">Generate topics above to see them here. Save any you want to keep for later.</p>}
          {generatedTopics.map((t, index) => {
            const key = t._id || `gen-${index}-${t.title}`
            const isExpanded = expandedTopicKeys.has(key)
            const isSaved = Boolean(t._id)
            const isSaving = savingTopicKey === key
            return (
              <div
                key={key}
                className="rounded-xl bg-white border border-slate-200 hover:border-slate-300 transition-colors overflow-hidden"
              >
                <div className="flex items-start gap-2 px-4 py-3">
                  <button
                    type="button"
                    aria-expanded={isExpanded}
                    aria-label={isExpanded ? "Hide topic details" : "Show topic details"}
                    className="mt-0.5 shrink-0 rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-colors"
                    onClick={() => toggleTopicExpanded(key)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                      className={`w-4 h-4 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="flex-1 min-w-0 text-left"
                    onClick={() => toggleTopicExpanded(key)}
                  >
                    <div className="flex items-center gap-2">
                      <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-slate-100 px-2 text-xs font-semibold text-slate-700">
                        {index + 1}
                      </span>
                      <p className="font-medium text-slate-900">{t.title}</p>
                      {isSaved && <span className="badge badge-success badge-sm">Saved</span>}
                    </div>
                    {!isExpanded && (t.summary || t.description) && (
                      <p className="text-sm text-slate-600 mt-0.5 line-clamp-2">{t.summary || t.description}</p>
                    )}
                  </button>
                  {!isSaved && (
                    <Button
                      size="sm"
                      variant="outline"
                      loading={isSaving}
                      onClick={() => handleSaveTopic(t, index)}
                    >
                      Save
                    </Button>
                  )}
                </div>
                {isExpanded && (
                  <div className="border-t border-slate-100 px-4 py-3 pl-11 bg-slate-50/60">
                    <GeneratedTopicDetails topic={t} />
                  </div>
                )}
              </div>
            )
          })}
        </div>
        </CardBody>
      </Card>

    </div>
  )
}

export default TopicIdeas
